/**
 * The portal's front door: a mobile number, then the code we text back.
 *
 * Two steps in one component rather than two routes, because the challenge is
 * only meaningful for the seconds between them and a route would invite
 * somebody to bookmark, share or reload it.
 *
 * Nothing here is remembered. The number is kept so the second step can say
 * which one it was sent to; the code is read from the field, posted, and
 * dropped. Neither reaches any storage API, and there is no "remember me" —
 * the session cookie the server sets is the only thing that persists.
 *
 * Every number on this screen is wrapped in `<Ltr>`. Persian copy with a bare
 * `+989121234567` in it does not render that number: the bidi algorithm
 * resolves the `+` and any adjacent digits against the paragraph, and the
 * result was `13+9891*****10` on Stage — the countdown and the phone number
 * fused into one run. Isolation, not direction, is the fix.
 */
import { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { Captcha } from '../components/Captcha'
import { securityMessage } from './securityMessages'
import { ApiError } from '../api/errors'
import { Ltr } from '../components/ui/Ltr'
import { OtpInput } from './OtpInput'
import {
  type DevelopmentCode,
  type OtpChallenge,
  developmentCode,
  requestCode,
  resendCode,
  verifyCode,
} from './otp'

/** The code length the server issues. Kept here only to size the field. */
const CODE_LENGTH = 6

const PERSIAN = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

function persianDigits(value: string): string {
  return value.replace(/[0-9]/g, (digit) => PERSIAN[Number(digit)] ?? digit)
}

/** `2:53`, not `173 ثانیه`. Nobody counts seconds in their head. */
function clock(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return persianDigits(`${minutes}:${String(rest).padStart(2, '0')}`)
}

/**
 * Seconds left, derived from when the clock started.
 *
 * Derived rather than decremented so a re-render, a slow tab or a backgrounded
 * page cannot drift it, and so nothing sets state synchronously on mount.
 */
function useCountdown(startedAt: number, seconds: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (seconds <= 0) return
    const timer = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(timer)
  }, [startedAt, seconds])
  return Math.max(0, seconds - Math.max(0, Math.floor((now - startedAt) / 1000)))
}

export function LoginPage({
  onSignedIn,
  error,
}: {
  onSignedIn: () => void
  /** A session probe that failed for a reason other than "not signed in". */
  error?: Error | null
}) {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaReset, setCaptchaReset] = useState(0)
  const inFlight = useRef(false)
  const [mobile, setMobile] = useState('')
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null)
  const [issuedAt, setIssuedAt] = useState(0)
  const [development, setDevelopment] = useState<DevelopmentCode | null>(null)
  const [copied, setCopied] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(error ? error.message : null)
  const codeField = useRef<HTMLInputElement>(null)

  const remaining = useCountdown(issuedAt, challenge?.expires_in ?? 0)
  const resendIn = useCountdown(issuedAt, challenge ? (challenge.resend_after ?? 60) : 0)

  async function ask(resend = false) {
    if (inFlight.current || !captchaToken || (resend && resendIn > 0)) return
    inFlight.current = true
    const token = captchaToken
    setCaptchaToken(null)
    setBusy(true)
    setMessage(null)
    setDevelopment(null)
    try {
      const issued = resend && challenge ? await resendCode(challenge.challenge_id, token) : await requestCode(mobile, token)
      setChallenge(issued)
      setIssuedAt(Date.now())
      setCode('')
      // Only ever answers on a Stage-style deployment; null everywhere else,
      // and then nothing below renders.
      setDevelopment(await developmentCode(issued.challenge_id))
      if (resend) setMessage(null)
      window.setTimeout(() => codeField.current?.focus(), 0)
    } catch (failure) {
      if (failure instanceof ApiError && failure.code === 'invalid_challenge') {
        setChallenge(null); setIssuedAt(0); setCode('')
      }
      // The server answers identically for a number it knows and one it does
      // not, so anything reaching here is about the input or a limit — never
      // about who exists.
      setMessage(
        failure instanceof ApiError
          ? securityMessage(failure)
          : 'ارسال کد ممکن نشد. لطفاً دوباره تلاش کنید.',
      )
    } finally {
      inFlight.current = false
      setCaptchaToken(null)
      setCaptchaReset(value => value + 1)
      setBusy(false)
    }
  }

  async function submit(submitted: string) {
    if (submitted.length !== CODE_LENGTH || busy || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setMessage(null)
    try {
      await verifyCode(challenge!.challenge_id, submitted)
      setCode('')
      setDevelopment(null)
      onSignedIn()
    } catch (failure) {
      if (failure instanceof ApiError && ['otp_attempts_exceeded', 'invalid_challenge'].includes(failure.code)) {
        setChallenge(null); setIssuedAt(0); setDevelopment(null); setCaptchaToken(null); setCaptchaReset(value => value + 1)
      }
      setCode('')
      setMessage(
        failure instanceof ApiError ? securityMessage(failure) : 'ورود انجام نشد. دوباره تلاش کنید.',
      )
      codeField.current?.focus()
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  /** Back to the number, with nothing from the abandoned challenge left. */
  function changeNumber() {
    setCaptchaToken(null)
    setCaptchaReset(value => value + 1)
    setChallenge(null)
    setIssuedAt(0)
    setCode('')
    setDevelopment(null)
    setCopied(false)
    setMessage(null)
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, maxWidth: 420, width: '100%' }}>
        <Typography variant="h1" gutterBottom>
          ورود به پنل
        </Typography>

        {message ? (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {message}
          </Alert>
        ) : null}

        {challenge === null ? (
          <Stack
            component="form"
            spacing={2}
            onSubmit={(event) => {
              event.preventDefault()
              void ask()
            }}
          >
            <Typography variant="body2" color="text.secondary">
              شمارهٔ موبایلی را وارد کنید که حساب شما با آن ثبت شده است. کد تأیید برای همان شماره
              پیامک می‌شود.
            </Typography>
            <TextField
              label="شمارهٔ موبایل"
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              autoComplete="tel"
              autoFocus
              required
              fullWidth
              placeholder="۰۹۱۲۱۲۳۴۵۶۷"
              slotProps={{
                htmlInput: {
                  dir: 'ltr',
                  inputMode: 'tel',
                  style: { direction: 'ltr', unicodeBidi: 'isolate', textAlign: 'left' },
                },
              }}
            />
            <Captcha action="client_otp_send" token={captchaToken} resetKey={captchaReset} onToken={setCaptchaToken} />
            <Button type="submit" variant="contained" disabled={!mobile || busy || !captchaToken} fullWidth>
              {busy ? 'در حال ارسال…' : 'دریافت کد تأیید'}
            </Button>
          </Stack>
        ) : (
          <Stack
            component="form"
            spacing={2}
            onSubmit={(event) => {
              event.preventDefault()
              void submit(code)
            }}
          >
            <Typography variant="body2" color="text.secondary">
              کد تأیید به شمارهٔ <Ltr mono>{challenge.mobile}</Ltr> ارسال شد.
            </Typography>

            {development ? (
              <Alert severity="warning" variant="outlined" data-testid="development-code">
                <AlertTitle sx={{ mb: 0.5 }}>محیط آزمایشی</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {development.notice}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Ltr
                    mono
                    sx={{ fontSize: '1.35rem', letterSpacing: '0.35em', fontWeight: 600 }}
                  >
                    {development.code}
                  </Ltr>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    onClick={() => {
                      void navigator.clipboard?.writeText(development.code)
                      setCode(development.code)
                      setCopied(true)
                    }}
                  >
                    {copied ? 'کپی شد' : 'کپی'}
                  </Button>
                </Stack>
              </Alert>
            ) : null}

            <OtpInput
              ref={codeField}
              label="کد تأیید"
              value={code}
              onChange={setCode}
              onComplete={(complete) => void submit(complete)}
              length={CODE_LENGTH}
              disabled={busy}
              autoFocus
              helperText={
                remaining > 0 ? `اعتبار کد: ${clock(remaining)}` : 'اعتبار کد تمام شد. کد تازه بگیرید.'
              }
            />

            <Captcha action="client_otp_resend" token={captchaToken} resetKey={captchaReset} onToken={setCaptchaToken} />
            <Button
              type="submit"
              variant="contained"
              disabled={code.length !== CODE_LENGTH || busy}
              fullWidth
            >
              {busy ? 'در حال بررسی…' : 'ورود'}
            </Button>

            <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
              <Link component="button" type="button" variant="body2" onClick={changeNumber}>
                تغییر شماره
              </Link>
              {resendIn > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  ارسال دوباره کد تا {clock(resendIn)}
                </Typography>
              ) : (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => void ask(true)}
                  disabled={busy || !captchaToken}
                >
                  ارسال دوباره کد
                </Link>
              )}
            </Stack>
            {resendIn === 0 ? (
              <Typography variant="caption" color="text.secondary">
                با ارسال دوباره، کد قبلی از کار می‌افتد.
              </Typography>
            ) : null}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          دسترسی به حساب را مدیر سامانه تنظیم می‌کند. اگر شمارهٔ شما ثبت نشده است، با پشتیبانی
          تماس بگیرید.
        </Typography>
      </Paper>
    </Box>
  )
}
